import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";
import AboutSection from "./components/AboutSection";
import ProjectsSection from "./components/ProjectsSection";
import SkillsSection from "./components/SkillsSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import useSmoothScroll from "./hooks/useSmoothScroll";
import {
  initScrollAnimations,
  pageTransition,
  magneticEffect,
} from "./utils/animations";

// Cursor Glow Component
const CursorGlow = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div
      className="cursor-glow"
      style={{
        left: mousePosition.x,
        top: mousePosition.y,
        opacity: isVisible ? 1 : 0,
      }}
    />
  );
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Resume download function
  const handleResumeDownload = () => {
    // Option 1: Direct download from public folder
    const resumeUrl = "/resume.pdf"; // Place your resume.pdf in the public folder
    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = "Nazmus_Sakib_Resume.pdf"; // Custom filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Option 2: Open in new tab (uncomment if you prefer this)
    // window.open(resumeUrl, '_blank');

    // Option 3: External URL (uncomment and replace with your actual resume URL)
    // window.open('https://your-resume-url.com/resume.pdf', '_blank');
  };

  // Smooth scroll function
  const smoothScrollTo = (targetId) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  useEffect(() => {
    // Add magnetic effect to buttons
    const magneticButtons = document.querySelectorAll(".magnetic-btn");
    magneticButtons.forEach((btn) => magneticEffect(btn));

    // Close mobile menu when clicking outside
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest(".mobile-nav-container")) {
        setMobileMenuOpen(false);
      }
    };

    // Close mobile menu on escape key
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [mobileMenuOpen]);

  return (
    <motion.header
      className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {/* Desktop Navigation */}
      <motion.nav
        className="hidden md:flex bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-2 py-2 items-center gap-1 shadow-2xl shadow-black/50"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <button
          className="px-5 py-2 rounded-full hover:bg-white/10 text-sm font-medium transition-colors text-gray-300 hover:text-white"
          onClick={() => smoothScrollTo("home")}
        >
          Home
        </button>
        <button
          className="px-5 py-2 rounded-full hover:bg-white/10 text-sm font-medium transition-colors text-gray-300 hover:text-white"
          onClick={() => smoothScrollTo("work")}
        >
          Work
        </button>
        <div className="w-px h-4 bg-white/10 mx-1"></div>
        <button
          className="px-5 py-2 rounded-full hover:bg-white/10 text-sm font-medium transition-colors text-gray-300 hover:text-white"
          onClick={() => smoothScrollTo("contact")}
        >
          Contact
        </button>
        <motion.button
          className="ml-1 pl-4 pr-5 py-2 bg-primary hover:bg-primary/90 rounded-full text-sm font-bold transition-all flex items-center gap-2 group text-white magnetic-btn"
          onClick={handleResumeDownload}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="magnetic-text">Resume</span>
          <motion.span
            className="material-symbols-outlined text-[16px]"
            animate={{ x: [0, 2, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            download
          </motion.span>
        </motion.button>
      </motion.nav>

      {/* Mobile Navigation */}
      <motion.div
        className="md:hidden w-full max-w-[340px] mobile-nav-container"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-5 py-3 flex items-center justify-between shadow-2xl shadow-black/50 relative z-50">
          <span className="font-display font-bold text-lg text-white tracking-wide">
            Sakib
          </span>
          <motion.button
            className="text-gray-300 hover:text-white transition-colors cursor-pointer p-1 rounded-full hover:bg-white/10 active:scale-95 duration-200"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle mobile menu"
          >
            <motion.span
              className="material-symbols-outlined block"
              animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {mobileMenuOpen ? "close" : "menu"}
            </motion.span>
          </motion.button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="absolute top-16 left-0 right-0 bg-[#0f1420]/95 backdrop-blur-2xl border border-white/10 rounded-3xl p-3 shadow-[0_20px_40px_rgba(0,0,0,0.6)] flex flex-col gap-1 z-40"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{
                duration: 0.3,
                ease: [0.32, 0.72, 0, 1],
              }}
            >
              <motion.button
                className="px-5 py-3 rounded-2xl hover:bg-white/5 text-base font-medium transition-colors text-gray-300 hover:text-white flex items-center gap-3 w-full text-left"
                onClick={() => smoothScrollTo("home")}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="material-symbols-outlined text-[20px] text-gray-500">
                  home
                </span>
                Home
              </motion.button>
              <motion.button
                className="px-5 py-3 rounded-2xl hover:bg-white/5 text-base font-medium transition-colors text-gray-300 hover:text-white flex items-center gap-3 w-full text-left"
                onClick={() => smoothScrollTo("work")}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="material-symbols-outlined text-[20px] text-gray-500">
                  grid_view
                </span>
                Work
              </motion.button>
              <motion.button
                className="px-5 py-3 rounded-2xl hover:bg-white/5 text-base font-medium transition-colors text-gray-300 hover:text-white flex items-center gap-3 w-full text-left"
                onClick={() => smoothScrollTo("contact")}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="material-symbols-outlined text-[20px] text-gray-500">
                  mail
                </span>
                Contact
              </motion.button>
              <div className="h-px bg-white/5 mx-2 my-1"></div>
              <motion.button
                className="px-5 py-3 bg-primary hover:bg-primary/90 rounded-2xl text-base font-bold transition-all flex items-center justify-between group text-white mt-1 shadow-lg shadow-primary/20 w-full"
                onClick={() => {
                  handleResumeDownload();
                  setMobileMenuOpen(false);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[20px]">
                    description
                  </span>
                  Resume
                </span>
                <motion.span
                  className="material-symbols-outlined text-[18px]"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  arrow_forward
                </motion.span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.header>
  );
};

// Hero Section Component
const HeroSection = () => {
  // Resume download function
  const handleResumeDownload = () => {
    // Option 1: Direct download from public folder
    const resumeUrl = "/resume.pdf"; // Place your resume.pdf in the public folder
    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = "Nazmus_Sakib_Resume.pdf"; // Custom filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Option 2: Open in new tab (uncomment if you prefer this)
    // window.open(resumeUrl, '_blank');

    // Option 3: External URL (uncomment and replace with your actual resume URL)
    // window.open('https://your-resume-url.com/resume.pdf', '_blank');
  };
  return (
    <motion.section
      id="home"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
      initial="initial"
      animate="animate"
      variants={{
        animate: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.3,
          },
        },
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 flex flex-col gap-8 order-2 lg:order-1 relative">
          <h1 className="absolute -top-32 -left-20 text-[180px] font-black text-white/[0.02] select-none pointer-events-none font-display hidden xl:block leading-none whitespace-nowrap">
            SAKIB
          </h1>
          <div className="space-y-6 relative">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm w-fit">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs font-medium tracking-wide text-gray-300 uppercase">
                Available for new projects
              </span>
            </div>
            <h1 className="text-5xl sm:text-7xl font-display font-bold leading-[1.1] tracking-tight">
              Building digital <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_4s_linear_infinite]">
                experiences
              </span>{" "}
              that matter.
            </h1>
            <p className="text-lg text-gray-400 max-w-xl leading-relaxed">
              I'm Nazmus Sakib, a Frontend Developer transforming complex
              challenges into elegant, high-performance web solutions. I don't
              just write code; I craft digital products.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-6 relative items-start sm:items-center">
            <button
              onClick={handleResumeDownload}
              className="h-14 px-8 rounded-2xl bg-white text-black font-bold text-lg hover:scale-105 transition-transform duration-200 flex items-center gap-2 shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)]"
            >
              <span>Resume</span>
              <span className="material-symbols-outlined">description</span>
            </button>
            <div className="flex items-center gap-3">
              <a
                aria-label="GitHub"
                className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
                href="https://github.com/Sakib8git"
              >
                <svg
                  className="w-5 h-5 fill-current"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"></path>
                </svg>
              </a>
              <a
                aria-label="LinkedIn"
                className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#0A66C2] hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
                href="https://www.linkedin.com/in/nazmus-sakib-anik"
              >
                <svg
                  className="w-5 h-5 fill-current"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                </svg>
              </a>
              <a
                aria-label="Facebook"
                className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#1877F2] hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
                href="https://www.facebook.com/sakib30278"
              >
                <svg
                  className="w-5 h-5 fill-current"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"></path>
                </svg>
              </a>
            </div>
          </div>
          <div className="pt-8 flex items-center gap-4 text-gray-500 text-sm font-medium relative">
            <span>Powering up with:</span>
            <div className="flex gap-4 items-center">
              <span
                className="material-symbols-outlined hover:text-white transition-colors cursor-help"
                title="React"
              >
                data_object
              </span>
              <span
                className="material-symbols-outlined hover:text-white transition-colors cursor-help"
                title="JavaScript"
              >
                javascript
              </span>
              <span
                className="material-symbols-outlined hover:text-white transition-colors cursor-help"
                title="Next.js"
              >
                layers
              </span>
              <span
                className="material-symbols-outlined hover:text-white transition-colors cursor-help"
                title="Node"
              >
                dns
              </span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end relative group">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-[3rem] rotate-6 scale-95 blur-2xl transition-all duration-500 group-hover:rotate-12 group-hover:scale-105"></div>
          <div className="relative w-full max-w-md aspect-[4/5] bg-gray-900 rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-1">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{
                backgroundImage:
                  'url("https://i.ibb.co.com/0ys5jjfX/linkin.jpg")',
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-gray-400 text-sm font-medium mb-1">
                    Based in
                  </p>
                  <p className="text-white font-display font-bold text-xl flex items-center gap-2">
                    <span className="material-symbols-outlined text-accent">
                      location_on
                    </span>
                    Khulna Bangladesh
                  </p>
                </div>
                {/* <div className="h-12 w-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                  <span className="material-symbols-outlined text-white">
                    terminal
                  </span>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default function App() {
  // Initialize smooth scrolling
  useSmoothScroll();

  useEffect(() => {
    // Initialize GSAP scroll animations
    initScrollAnimations();
  }, []);

  return (
    <AnimatePresence>
      <motion.div className="dark" {...pageTransition}>
        <CursorGlow />
        <div className="bg-dark-bg text-white font-sans overflow-x-hidden selection:bg-primary selection:text-white">
          {/* Background Effects */}
          <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:24px_24px] opacity-20"></div>
            <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-primary/20 rounded-full blur-[120px] mix-blend-screen animate-float"></div>
            <div
              className="absolute bottom-[-10%] left-[-20%] w-[600px] h-[600px] bg-accent/15 rounded-full blur-[100px] mix-blend-screen"
              style={{ animationDelay: "-3s" }}
            ></div>
          </div>

          <Header />

          <main className="relative z-10 flex flex-col justify-center min-h-screen pt-32 pb-20">
            <HeroSection />
            <AboutSection />
            <ProjectsSection />
            <SkillsSection />
            <ContactSection />
          </main>

          <Footer />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
